/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useContext } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/efta_logo.svg";
import brandDark from "assets/images/efta_logo.svg";
import { UserProvider, UserContext } from "context/UserContext";
import { SalesProvider } from "context/SalesContext";
import { OrdersProvider } from "context/OrdersContext";
import { LogisticsProvider } from "context/LogisticsContext";
import SignIn from "layouts/authentication/sign-in";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { APP_TITLE } from "config/apiConfig";

// Initialize axios instance for refresh token functionality
import "./services/axiosConfig";

const coreRoles = ["ROLE_MANAGER", "ROLE_SALES_RESPONSIBLE", "ROLE_CUSTOMER"];

const hasCoreRole = (roles = []) => roles.some((role) => coreRoles.includes(role));

const getDefaultRoute = (user) => {
  if (!user) {
    return "/authentication/sign-in";
  }

  const roles = Array.isArray(user.roles) ? user.roles : [];
  return hasCoreRole(roles) ? "/orders/create" : "/demand";
};

function DefaultRedirect() {
  const { user } = useContext(UserContext);

  return <Navigate to={getDefaultRoute(user)} replace />;
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <UserProvider>
      <OrdersProvider>
        <LogisticsProvider>
          <SalesProvider>
            <CacheProvider value={rtlCache}>
              <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
                <CssBaseline />
                {layout === "dashboard" && pathname !== "/authentication/sign-in" && (
                  <>
                    <Sidenav
                      color={sidenavColor}
                      brand={
                        (transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite
                      }
                      brandName="EFTA Tuz"
                      routes={routes}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    />
                    <Configurator />
                    {configsButton}
                  </>
                )}
                {layout === "vr" && <Configurator />}
                <Routes>
                  <Route path="/authentication/sign-in" element={<SignIn />} />
                  <Route path="/" element={<DefaultRedirect />} />
                  {getRoutes(routes)}
                  <Route path="*" element={<DefaultRedirect />} />
                </Routes>
                <ToastContainer position="top-right" autoClose={3000} />
              </ThemeProvider>
            </CacheProvider>
          </SalesProvider>
        </LogisticsProvider>
      </OrdersProvider>
    </UserProvider>
  ) : (
    <UserProvider>
      <OrdersProvider>
        <LogisticsProvider>
          <SalesProvider>
            <ThemeProvider theme={darkMode ? themeDark : theme}>
              <CssBaseline />
              {layout === "dashboard" && pathname !== "/authentication/sign-in" && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand={
                      (transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite
                    }
                    brandName="EFTA Tuz"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <Configurator />
                  {configsButton}
                </>
              )}
              {layout === "vr" && <Configurator />}
              <Routes>
                <Route path="/authentication/sign-in" element={<SignIn />} />
                <Route path="/" element={<DefaultRedirect />} />
                {getRoutes(routes)}
                <Route path="*" element={<DefaultRedirect />} />
              </Routes>
              <ToastContainer position="top-right" autoClose={3000} />
            </ThemeProvider>
          </SalesProvider>
        </LogisticsProvider>
      </OrdersProvider>
    </UserProvider>
  );
}
